(() => {
    "use strict";
    const PAGE_ROOT = "#site-page";
    const PUBLIC_PREFIXES = ["/", "/palestrantes", "/blog", "/sobre-nos", "/contato", "/solicitar-orcamento", "/trabalhos"];
    const PAGE_CACHE_LIMIT = 10;
    const FETCH_HEADERS = {
        "X-Requested-With": "XMLHttpRequest",
        Accept: "text/html",
    };
    let currentPathname = window.location.pathname;
    let lastTrackedLocation = window.location.href;
    let navigating = false;
    const prefetchingKeys = new Set();
    const pageCache = new Map();
    const pageCacheOrder = [];
    function toUrl(url) {
        try {
            return new URL(url, window.location.origin);
        } catch (error) {
            return null;
        }
    }
    function isSitePage(url) {
        if (!url || url.origin !== window.location.origin) {
            return false;
        }
        const path = url.pathname.replace(/\/$/, "") || "/";
        return PUBLIC_PREFIXES.some((prefix) => {
            if (prefix === "/") {
                return path === "/";
            }
            return path === prefix || path.startsWith(`${prefix}/`);
        });
    }
    function fragmentName(url) {
        return url.searchParams.get("fragment") || "";
    }
    function shouldPrefetch(url) {
        if (!url || !isSitePage(url)) {
            return false;
        }
        if (url.pathname === currentPathname && url.search === window.location.search) {
            return false;
        }
        return !["cards", "results", "posts"].includes(fragmentName(url));
    }
    function cacheKey(url) {
        const keyUrl = new URL(url.toString());
        keyUrl.hash = "";
        keyUrl.searchParams.delete("fragment");
        const params = [...keyUrl.searchParams.entries()].sort((left, right) => left[0].localeCompare(right[0]));
        keyUrl.search = "";
        params.forEach(([name, value]) => keyUrl.searchParams.append(name, value));
        return keyUrl.pathname + keyUrl.search;
    }
    function pageRequestUrl(url) {
        const requestUrl = new URL(url.toString());
        requestUrl.hash = "";
        requestUrl.searchParams.delete("fragment");
        requestUrl.searchParams.set("fragment", "page");
        return requestUrl;
    }
    function rememberCache(key, html) {
        if (pageCache.has(key)) {
            const index = pageCacheOrder.indexOf(key);
            if (index >= 0) {
                pageCacheOrder.splice(index, 1);
            }
        }
        pageCache.set(key, html);
        pageCacheOrder.push(key);
        while (pageCacheOrder.length > PAGE_CACHE_LIMIT) {
            const oldest = pageCacheOrder.shift();
            if (oldest) {
                pageCache.delete(oldest);
            }
        }
    }
    async function fetchPageHtml(url) {
        const key = cacheKey(url);
        if (pageCache.has(key)) {
            return { html: pageCache.get(key), cached: true };
        }
        const response = await fetch(pageRequestUrl(url).toString(), { headers: FETCH_HEADERS });
        if (!response.ok) {
            throw new Error(`page-nav-${response.status}`);
        }
        const html = (await response.text()).trim();
        rememberCache(key, html);
        return { html, cached: false };
    }
    function prefetchPage(url) {
        if (!shouldPrefetch(url)) {
            return;
        }
        const key = cacheKey(url);
        if (pageCache.has(key) || prefetchingKeys.has(key)) {
            return;
        }
        prefetchingKeys.add(key);
        fetchPageHtml(url)
            .catch(() => {})
            .finally(() => {
                prefetchingKeys.delete(key);
            });
    }
    function prefetchFromLink(link) {
        const url = toUrl(link.href);
        prefetchPage(url);
    }
    function prefetchNavLinks() {
        const seen = new Set();
        document.querySelectorAll("#site-header .site-menu a[href], .mega-menu a[href]").forEach((link) => {
            const url = toUrl(link.href);
            if (!shouldPrefetch(url)) {
                return;
            }
            const key = cacheKey(url);
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            prefetchPage(url);
        });
    }
    /**
     * O pré-carregamento de todo o menu rodava logo após o load (6 páginas completas renderizadas no servidor, ~1,4 MB
     * descomprimidos) e competia com a própria página. Agora só começa depois da primeira interação real do visitante;
     * pairar/focar/tocar num link já pré-carrega aquela página (ver `prefetchFromLink`).
     */
    function scheduleNavPrefetch() {
        const events = ["pointerdown", "keydown", "touchstart", "wheel"];
        const start = () => {
            events.forEach((type) => window.removeEventListener(type, start, true));
            if ("requestIdleCallback" in window) {
                window.requestIdleCallback(prefetchNavLinks, { timeout: 2500 });
                return;
            }
            window.setTimeout(prefetchNavLinks, 1200);
        };
        events.forEach((type) => window.addEventListener(type, start, { capture: true, passive: true, once: true }));
    }
    function shouldIntercept(event, link) {
        if (event.defaultPrevented || event.button !== 0) {
            return false;
        }
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return false;
        }
        if (link.target && link.target !== "_self") {
            return false;
        }
        if (link.hasAttribute("download") || link.getAttribute("rel") === "external") {
            return false;
        }
        const url = toUrl(link.href);
        if (!isSitePage(url)) {
            return false;
        }
        if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) {
            return false;
        }
        if (["cards", "results", "posts"].includes(fragmentName(url))) {
            return false;
        }
        if (link.hasAttribute("data-cesta-pick")) {
            return false;
        }
        if (link.closest("#cesta-pick-dialog") && !link.hasAttribute("data-cesta-pick-page")) {
            return false;
        }
        if (url.pathname === window.location.pathname && url.search === window.location.search) {
            return false;
        }
        return true;
    }
    function closeChrome() {
        document.querySelector(".mega-menu")?.classList.remove("active");
        document.querySelector(".mega-menu")?.setAttribute("aria-hidden", "true");
        document.querySelector(".btn-mega-menu")?.setAttribute("aria-expanded", "false");
        document.querySelectorAll("dialog.site-filter-dialog[open]").forEach((dialog) => {
            if (typeof dialog.close === "function") {
                dialog.close();
            }
        });
        document.querySelector(".cesta-orcamento")?.classList.remove("active");
        const cesta = document.querySelector(".cesta-orcamento-site");
        if (cesta) {
            cesta.classList.remove("is-open");
            cesta.setAttribute("aria-hidden", "true");
            cesta.inert = true;
        }
        const overlay = document.querySelector(".cesta-orcamento-site-overlay");
        if (overlay) {
            overlay.hidden = true;
            overlay.classList.remove("is-open");
        }
        document.querySelector("#site-header .btn-orcamento")?.setAttribute("aria-expanded", "false");
        const picker = document.getElementById("cesta-pick-dialog");
        if (picker?.open) {
            picker.close();
        }
        document.body.classList.remove("no-scroll");
    }
    function syncActiveMenu(pathname) {
        const current = pathname.replace(/\/$/, "") || "/";
        document.querySelectorAll("#site-header .site-menu a, .mega-menu a").forEach((link) => {
            const url = toUrl(link.href);
            if (!url) {
                return;
            }
            const linkPath = url.pathname.replace(/\/$/, "") || "/";
            const active = linkPath === "/"
                ? current === "/"
                : current === linkPath || current.startsWith(`${linkPath}/`);
            link.classList.toggle("active", active);
        });
    }
    function syncMetaContent(selector, value) {
        const meta = document.querySelector(selector);
        if (meta && typeof value === "string" && value !== "") {
            meta.setAttribute("content", value);
        }
    }
    /**
     * O fragmento traz o mesmo `@graph` que um GET completo montaria. Substituir o bloco inteiro
     * (em vez de só editar o nó WebPage) evita deixar Person/FAQPage da página anterior no ar.
     */
    function syncStructuredData(page) {
        const schema = page.getAttribute("data-schema");
        const script = document.querySelector('script[type="application/ld+json"][data-site-schema]');
        if (script && schema) {
            script.textContent = schema;
        }
    }
    /**
     * O cookie de atribuição é httpOnly: só o servidor sabe o valor atual. Os formulários dentro
     * do `<main>` vêm novos a cada fragmento, mas o do WhatsApp vive no rodapé e ficaria com o
     * `gclid`/UTM da primeira página da sessão.
     */
    function syncCsrfTokens() {
        if (typeof regenerateCsrfToken !== "function") {
            return;
        }
        const field = document.querySelector(`${PAGE_ROOT} form [name=csrf_token]`);
        const form = field?.closest("form");
        if (form) {
            regenerateCsrfToken(form);
        }
    }
    function syncCampaignAttribution(page) {
        const raw = page.getAttribute("data-campaign-attribution");
        if (raw === null) {
            return;
        }
        let values = {};
        try {
            values = JSON.parse(raw || "{}") || {};
        } catch (error) {
            return;
        }
        document.querySelectorAll("#form-whatsapp-contact").forEach((form) => {
            form.querySelectorAll("[data-campaign-attribution-field]").forEach((field) => field.remove());
            Object.keys(values).forEach((name) => {
                const input = document.createElement("input");
                input.type = "hidden";
                input.name = name;
                input.value = String(values[name]);
                input.setAttribute("data-campaign-attribution-field", "");
                form.appendChild(input);
            });
        });
    }
    function trackVirtualPageview() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "virtualPageview",
            page_path: window.location.pathname + window.location.search,
            page_location: window.location.href,
            page_title: document.title,
            page_referrer: lastTrackedLocation,
        });
        lastTrackedLocation = window.location.href;
    }
    function syncDocument(page, url) {
        const title = page.getAttribute("data-title");
        const description = page.getAttribute("data-description");
        const bodyClass = page.getAttribute("data-body-class") || "";
        const canonical = page.getAttribute("data-canonical");
        if (title) {
            document.title = title;
        }
        syncMetaContent('meta[name="description"]', description);
        syncMetaContent('meta[name="robots"]', page.getAttribute("data-robots"));
        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink && canonical) {
            canonicalLink.setAttribute("href", canonical);
        }
        const ogTitle = page.getAttribute("data-og-title");
        const ogDescription = page.getAttribute("data-og-description");
        const ogImage = page.getAttribute("data-og-image");
        const ogImageAlt = page.getAttribute("data-og-image-alt");
        syncMetaContent('meta[property="og:type"]', page.getAttribute("data-og-type"));
        syncMetaContent('meta[property="og:title"]', ogTitle);
        syncMetaContent('meta[property="og:description"]', ogDescription);
        syncMetaContent('meta[property="og:url"]', page.getAttribute("data-og-url"));
        syncMetaContent('meta[property="og:image"]', ogImage);
        syncMetaContent('meta[property="og:image:alt"]', ogImageAlt);
        syncMetaContent('meta[name="twitter:title"]', ogTitle);
        syncMetaContent('meta[name="twitter:description"]', ogDescription);
        syncMetaContent('meta[name="twitter:image"]', ogImage);
        syncMetaContent('meta[name="twitter:image:alt"]', ogImageAlt);
        syncStructuredData(page);
        document.body.className = bodyClass;
        syncActiveMenu(url.pathname);
        closeChrome();
        syncCampaignAttribution(page);
        const origem = document.querySelector("#form-whatsapp-contact [name='paginaOrigem']");
        if (origem) {
            origem.value = url.pathname + url.search;
        }
        // reCAPTCHA fica por conta de `site/core/recaptcha.js`: o loader é
        // delegado no document e cobre formulários montados nesta navegação.
    }
    function parsePageFragment(html) {
        const template = document.createElement("template");
        template.innerHTML = html;
        return template.content.querySelector(PAGE_ROOT);
    }
    document.addEventListener(
        "mouseover",
        (event) => {
            const link = event.target.closest("a[href]");
            if (link) {
                prefetchFromLink(link);
            }
        },
        { passive: true }
    );
    document.addEventListener(
        "touchstart",
        (event) => {
            const link = event.target.closest("a[href]");
            if (link) {
                prefetchFromLink(link);
            }
        },
        { passive: true }
    );
    document.addEventListener(
        "focusin",
        (event) => {
            const link = event.target.closest("a[href]");
            if (link) {
                prefetchFromLink(link);
            }
        }
    );
    function ensurePageStyles() {
        const home = document.querySelector('link[data-site-full-css]');
        if (!home) return Promise.resolve();
        return new Promise((resolve, reject) => {
            const full = document.createElement('link');
            full.rel = 'stylesheet';
            full.href = home.dataset.siteFullCss;
            const timeout = window.setTimeout(() => { full.remove(); reject(new Error('page-css-timeout')); }, 15000);
            full.onload = () => { window.clearTimeout(timeout); home.remove(); resolve(); };
            full.onerror = () => { window.clearTimeout(timeout); full.remove(); reject(new Error('page-css-load')); };
            home.after(full);
        });
    }
    async function renderPage(url, { push = true } = {}) {
        const current = document.querySelector(PAGE_ROOT);
        if (!current || navigating) {
            window.location.assign(url.toString());
            return;
        }
        navigating = true;
        const key = cacheKey(url);
        const hasCachedPage = pageCache.has(key);
        if (!hasCachedPage) {
            document.documentElement.classList.add("is-page-pending");
        }
        try {
            const [{ html, cached }] = await Promise.all([fetchPageHtml(url), ensurePageStyles()]);
            const next = parsePageFragment(html);
            if (!next) {
                throw new Error("page-nav-empty");
            }
            let applied = false;
            const apply = () => {
                current.replaceWith(next);
                syncDocument(next, url);
                currentPathname = url.pathname;
                if (push) {
                    history.pushState({ sitePage: true }, "", url.pathname + url.search + url.hash);
                }
                window.scrollTo(0, 0);
                applied = true;
            };
            const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            const instantSwap = cached || hasCachedPage;
            if (!instantSwap && !reducedMotion && typeof document.startViewTransition === "function") {
                const transition = document.startViewTransition(apply);
                transition.finished.catch(() => {});
                // Página pesada faz o navegador abortar a transição por timeout. O apply() já
                // rodou nesse ponto: cair no catch aqui recarregaria a página sem motivo.
                await transition.updateCallbackDone.catch((error) => {
                    if (!applied) {
                        throw error;
                    }
                });
            } else {
                apply();
            }
            syncCsrfTokens();
            trackVirtualPageview();
            document.dispatchEvent(new CustomEvent("site:navigated", { detail: { url: url.toString() } }));
        } catch (error) {
            window.location.assign(url.toString());
        } finally {
            navigating = false;
            document.documentElement.classList.remove("is-page-pending");
        }
    }
    document.addEventListener("click", (event) => {
        const link = event.target.closest("a[href]");
        if (!link || !shouldIntercept(event, link)) {
            return;
        }
        event.preventDefault();
        const url = toUrl(link.href);
        if (url) {
            renderPage(url);
        }
    });
    document.addEventListener("submit", (event) => {
        if (event.defaultPrevented) {
            return;
        }
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) {
            return;
        }
        if ((form.getAttribute("method") || "get").toLowerCase() !== "get") {
            return;
        }
        if (form.hasAttribute("data-cesta-pick-form") || form.closest("#cesta-pick-dialog")
            || form.hasAttribute("data-perfil-search") || form.id === "catalog-search-form") {
            return;
        }
        const url = toUrl(form.getAttribute("action") || window.location.href);
        if (!isSitePage(url) || (url.pathname === window.location.pathname && url.search === window.location.search)) {
            return;
        }
        event.preventDefault();
        new FormData(form).forEach((value, key) => {
            const next = String(value).trim();
            if (next !== "") {
                url.searchParams.set(key, next);
            }
        });
        renderPage(url);
    });
    /**
     * Lead enviado limpa o cookie de atribuição no servidor: fragmentos guardados em memória
     * ainda carregam o `gclid` antigo e reinjetariam a atribuição já consumida.
     */
    document.addEventListener("motiveacao:lead-converted", () => {
        pageCache.clear();
        pageCacheOrder.length = 0;
        document
            .querySelectorAll("#form-whatsapp-contact [data-campaign-attribution-field]")
            .forEach((field) => field.remove());
    });
    window.addEventListener("popstate", () => {
        const url = toUrl(window.location.href);
        if (!isSitePage(url)) {
            return;
        }
        if (url.pathname === currentPathname && url.search === window.location.search) {
            return;
        }
        renderPage(url, { push: false });
    });
    scheduleNavPrefetch();
})();
